/* The second sidebar of an agent's Spaces tab, in the Knowledge rail's recipe: New table,
   a search when the tables grow, People (kept by Definable) then the agent's own tables
   with their row counts, and at the foot what the space holds and a way to ask Definable
   to change it. A table's ⋮ opens its settings. */
import React, { useMemo, useState } from "react";
import { EllipsisVertical, Lock, Plus, Table2, UsersRound } from "lucide-react";

import PixelAvatar from "@/shared/components/PixelAvatar";
import { BrandButton, GhostButton, SearchInput } from "@/shared/components/ui-kit";
import Logo from "@/shared/components/Logo";
import { cn } from "@/shared/lib/cn";

import type { Agent, SpaceTable } from "@/shared/types";

/** Past this many tables the rail offers a search. */
const SEARCH_FROM = 8;

export default function SpacesRail({ agent, tables, open, onOpen, onNewTable, onSettings, onAskDefinable }: {
  agent: Agent;
  tables: SpaceTable[];
  /** The open table's id, or null on the schema or first-visit page. */
  open: string | null;
  onOpen: (tableId: string) => void;
  onNewTable: () => void;
  onSettings: (table: SpaceTable) => void;
  onAskDefinable?: () => void;
}) {
  const [query, setQuery] = useState("");
  const shown = useMemo(() => {
    const wanted = query.trim().toLowerCase();
    return wanted ? tables.filter((table) => table.name.toLowerCase().includes(wanted)) : tables;
  }, [tables, query]);
  const own = tables.filter((table) => !table.system);
  const rows = own.reduce((sum, table) => sum + (table.row_count ?? 0), 0);

  return (
    <aside aria-label={`${agent.name}'s tables`} className="hidden md:flex w-[232px] lg:w-[264px] shrink-0 min-h-0 flex-col bg-[var(--surface-sunk)] border-r hairline">
      <div className="h-12 shrink-0 flex items-center px-3">
        <BrandButton onClick={onNewTable} className="w-full justify-center">
          <Plus className="w-3.5 h-3.5" strokeWidth={2} /> New table
        </BrandButton>
      </div>
      {tables.length > SEARCH_FROM && (
        <div className="shrink-0 px-3 pb-1.5">
          <SearchInput raised value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a table" aria-label="Find a table" />
        </div>
      )}
      <div className="shrink-0 pl-4 pr-2 pt-1 pb-1 section-label">{agent.name}'s tables</div>
      <nav aria-label="Tables" className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 pb-3 space-y-[1px]">
        {shown.map((table) => (
          <div key={table.id} className="group relative">
            <button
              type="button"
              aria-current={table.id === open ? "page" : undefined}
              onClick={() => onOpen(table.id)}
              title={table.name}
              className={cn(
                "press w-full h-[30px] flex items-center gap-2 pl-2 rounded-[8px] text-left text-[13.5px]",
                table.system ? "pr-2" : "pr-8",
                table.id === open
                  ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
                  : "text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]",
              )}
            >
              {table.system ? (
                <UsersRound className="w-4 h-4 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
              ) : (
                <Table2 className="w-4 h-4 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
              )}
              <span className="flex-1 min-w-0 truncate">{table.name}</span>
              {table.system ? (
                <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-[var(--text-quaternary)] font-normal">
                  <Lock className="w-2.5 h-2.5" strokeWidth={2} /> System
                </span>
              ) : (
                <span className="shrink-0 text-[11px] font-mono tabular-nums text-[var(--text-quaternary)] font-normal">
                  {(table.row_count ?? 0).toLocaleString("en-US")}
                </span>
              )}
            </button>
            {!table.system && (
              <button
                type="button"
                aria-label={`${table.name} settings`}
                title="Table settings"
                onClick={() => onSettings(table)}
                className="press absolute right-1 top-[3px] w-6 h-6 rounded-[6px] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)] opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
              >
                <EllipsisVertical className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
            )}
          </div>
        ))}
        {!own.length && <p className="m-0 px-2 py-1.5 text-[11.5px] italic text-[var(--text-quaternary)]">No tables yet</p>}
        <button
          type="button"
          onClick={onNewTable}
          className="press w-full h-[30px] flex items-center gap-2 px-2 rounded-[8px] text-left text-[13.5px] text-[var(--text-quaternary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
        >
          <Plus className="w-4 h-4 shrink-0" strokeWidth={1.75} /> New table
        </button>
      </nav>
      <div className="shrink-0 border-t hairline px-3.5 pt-3 pb-3.5 space-y-2">
        <div className="flex items-center gap-2 text-[12px] text-[var(--text-tertiary)]">
          <PixelAvatar seed={agent.seed} hue={agent.hue} size={18} />
          <span className="truncate">
            {own.length ? (
              <>
                <span className="font-mono">{own.length}</span> {own.length === 1 ? "table" : "tables"} ·{" "}
                <span className="font-mono">{rows.toLocaleString("en-US")}</span> rows
              </>
            ) : (
              "People only"
            )}
          </span>
        </div>
        {onAskDefinable && (
          <GhostButton onClick={onAskDefinable} className="w-full justify-start h-7 px-2.5">
            <Logo size={13} /> Ask Definable
          </GhostButton>
        )}
      </div>
    </aside>
  );
}
